import { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import SearchIcon from '@material-ui/icons/Search';
import makeStyles from '@material-ui/styles/makeStyles';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import useDebounce from 'hooks/common/useDebounce';
import { get, put } from 'helpers/apiHelpers';
import { DialogLoader } from 'components/DialogLoader';
import Card from 'components/Card/Card';
import GridItem from 'components/Grid/GridItem';
import CardBody from 'components/Card/CardBody';
import CustomInput from 'components/CustomInput/CustomInput';
import GridContainer from 'components/Grid/GridContainer';
import FormSelectSingle from 'components/FormSelect/FormSelectSingle';
import ObservableSection from 'components/ObservableSection/ObservableSection';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import { toast } from 'react-toastify';

import TRANSLATIONS_MODE from './constants';
import TableInputContainer from './TableInputContainer';

const useStyles = makeStyles({
  ...extendedFormsStyle,
  ...buttonsStyle,
  container: { marginTop: '15px' },
  // eslint-disable-next-line camelcase -- FIXME
  string_cell: {
    maxWidth: 300,
  },
  // eslint-disable-next-line camelcase -- FIXME
  translations_cell: {
    minWidth: 200,
  },
  descriptionParagraph: {
    textAlign: 'right',
    margin: '15px 15px 8px 0',
  },
  tableParent: { overflow: 'scroll', maxHeight: '520px' },
});

let newTranslations = {};

const TranslationsTable = ({ catalog, subCatalog = 'default' }) => {
  const [filterValue, setFilterValue] = useState('');
  const [batches, setBatches] = useState([]);
  const [languages, setLanguages] = useState(null);
  const [languageOrder, setLanguageOrder] = useState(null);
  const [translations, setTranslations] = useState(null);
  const [urlSubCatalog, setUrlSubCatalog] = useState(subCatalog);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedFilterValue = useDebounce(filterValue);

  const classes = useStyles();
  const { t } = useTranslation();
  useEffect(() => {
    // eslint-disable-next-line no-use-before-define -- FIXME
    geteFilteredTranslationKeysBatches(debouncedFilterValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [debouncedFilterValue]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const languagesResponse = await get('/languages');
      const langOrder = Object.values(languagesResponse['hydra:member']).map(
        val => val.isoCode
      );
      const translationsResponse = await get(
        `/translations/${catalog}/${urlSubCatalog}`
      );

      // eslint-disable-next-line no-use-before-define -- FIXME
      const newBatches = getTranslationKeysBatches({
        batchSize: 10,
        translations: Object.entries(translationsResponse ?? {}),
      });

      setBatches(newBatches);
      setLanguages(languagesResponse['hydra:member']);
      setLanguageOrder(langOrder);
      setTranslations(translationsResponse);
      newTranslations = {};

      setIsLoading(false);
    })();
  }, [catalog, urlSubCatalog]);

  useEffect(() => {
    setUrlSubCatalog(subCatalog);
  }, [subCatalog]);

  const changeTranslationValue = (string, language, value) => {
    newTranslations = produce(newTranslations, draftState => {
      if (value === translations[string][language]) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
        delete draftState[string][language];
        if (Object.keys(draftState[string]).length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
          delete draftState[string];
        }
        return;
      }
      if (draftState[string]) {
        draftState[string][language] = value;
      } else {
        draftState[string] = { [language]: value };
      }
    });
  };

  const handleSubmit = async () => {
    const translationObject = {
      [catalog]: {
        [urlSubCatalog]: newTranslations,
      },
    };

    try {
      await put('/translations/update', translationObject);
      toast.success(t('success.changesSaved'));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME
    } catch (e) {
      toast.error(t('notify.cannotSave'));
    }
  };

  // eslint-disable-next-line no-shadow -- FIXME
  const getTranslationKeysBatches = ({ batchSize, translations }) => {
    const newBatches = [];
    const numberOfBatches = Math.ceil(translations.length / batchSize);

    // eslint-disable-next-line no-plusplus -- FIXME
    for (let index = 0; index < numberOfBatches; index++) {
      newBatches.push(
        translations.slice(index * batchSize, (index + 1) * batchSize)
      );
    }

    return newBatches;
  };

  // eslint-disable-next-line no-shadow -- FIXME
  const geteFilteredTranslationKeysBatches = filterValue => {
    if (isEmpty(translations)) {
      return;
    }

    if (isEmpty(filterValue)) {
      const newBatches = getTranslationKeysBatches({
        batchSize: 10,
        translations: Object.entries(translations ?? {}),
      });

      // eslint-disable-next-line consistent-return -- FIXME
      return setBatches(newBatches);
    }

    setIsLoading(true);
    const translationsEntries = Object.entries(translations ?? {});

    const filteredTranslationsEntries = translationsEntries.filter(
      ([key, translationValues]) => {
        const values = Object.values(translationValues);
        return (
          key.includes(filterValue) ||
          values.some(value => value.includes(filterValue))
        );
      }
    );

    const newBatches = getTranslationKeysBatches({
      batchSize: 10,
      translations: filteredTranslationsEntries,
    });

    setBatches(newBatches);
    // eslint-disable-next-line consistent-return -- FIXME
    return setIsLoading(false);
  };

  return (
    <>
      <DialogLoader loaderState={isLoading} text={t('common.loading')} />

      {languages && translations && !isLoading && (
        <GridContainer className={classes.container}>
          <GridItem xs={12}>
            <Card>
              <CardBody>
                <GridContainer>
                  {catalog === TRANSLATIONS_MODE.api && (
                    <GridItem md={6}>
                      <FormSelectSingle
                        classes={classes}
                        options={[
                          { value: 'messages', name: 'Messages' },
                          { value: 'validators', name: 'Validators' },
                        ]}
                        mapBy="name"
                        trackBy="value"
                        value={urlSubCatalog}
                        handleChange={e => setUrlSubCatalog(e.target.value)}
                      />
                    </GridItem>
                  )}
                  <GridItem xs={12} sm={5} md={4} lg={3}>
                    <CustomInput
                      id="filterValue"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        placeholder: t(
                          '$*translationsTable.searchBart.placeholder'
                        ),
                        value: filterValue,
                        onChange: e => setFilterValue(e.target.value),
                        endAdornment: <SearchIcon />,
                      }}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <div className={classes.tableParent}>
                      <Table className={classes.table}>
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.string_cell}>
                              String
                            </TableCell>
                            {languages.map(lang => (
                              <TableCell
                                key={lang.name}
                                className={classes.translations_cell}
                              >
                                {lang.name}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batches.map((batch, index) => (
                            <ObservableSection
                              id={`batch-${index}`}
                              key={`batch-${index}`}
                              preload={index === 0}
                              enabled={true}
                            >
                              {batch.map(([string, translationValues]) => (
                                <TableRow
                                  key={`${catalog}-${urlSubCatalog}-${string}`}
                                >
                                  <TableCell className={classes.string_cell}>
                                    {string}
                                  </TableCell>
                                  {languageOrder.map(lang => (
                                    <TableCell
                                      key={`${catalog}-${urlSubCatalog}-${string}-${lang}`}
                                      className={classes.translations_cell}
                                    >
                                      <TableInputContainer
                                        string={string}
                                        translation={translationValues[lang]}
                                        language={lang}
                                        changeTranslationValue={
                                          changeTranslationValue
                                        }
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </ObservableSection>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </GridItem>
                </GridContainer>
                {catalog === TRANSLATIONS_MODE.clientPanel && (
                  <p className={classes.descriptionParagraph}>
                    {t('translations.clientPanel.description')}
                  </p>
                )}
                <FormControlButtons
                  classes={classes}
                  submitText={t('common.shared.save')}
                  handleSubmit={handleSubmit}
                />
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      )}
    </>
  );
};

const enhance = compose(
  connect(({ Auth: { selectedBrand } }) => ({
    selectedBrand,
  }))
);

export default enhance(TranslationsTable);
